/**
 * Discount display contract — binding for the renderer (T04) and consumers
 * (T05 kiosk app, T06 builder preview).
 *
 * The seven elements and the state table come verbatim from
 * `plans/designs/phase-4-kiosk-ui-design.md` §"Discount component — required
 * seven elements and states". Every value is an opaque, server-returned
 * display string: the renderer NEVER computes, compares, or transforms money.
 * Each field is independently optional — absent fields are omitted, never
 * fabricated and never inferred.
 */
export const DISCOUNT_FIELD_KEYS = Object.freeze([
  'originalPrice',
  'discountedPrice',
  'percentOff',
  'amountSaved',
  'expiry',
  'promoLabel',
  'conditions',
] as const);

export type DiscountFieldKey = (typeof DISCOUNT_FIELD_KEYS)[number];

/** The seven required discount elements, all independently optional server strings. */
export interface DiscountDisplayFields {
  readonly originalPrice?: string;
  readonly discountedPrice?: string;
  readonly percentOff?: string;
  readonly amountSaved?: string;
  readonly expiry?: string;
  readonly promoLabel?: string;
  readonly conditions?: string;
}

/** Frozen discount states from the design doc state table, in table order. */
export const DISCOUNT_STATES = Object.freeze([
  'no-discount',
  'applied',
  'ineligible',
  'expired',
  'pending-quote',
  'quote-failed',
  'updated-on-requote',
] as const);

export type DiscountState = (typeof DISCOUNT_STATES)[number];

/**
 * Boolean presentation flags per state. These encode the design doc's copy
 * rules ("strike through only when the server provides it and an actual
 * discount applies", "do not display discounted price as current", "no 'save'
 * claim") without touching any value.
 */
export interface DiscountPresentation {
  /** Show `originalPrice` with a visual strike-through (accessible label still required). */
  readonly strikeThroughOriginal: boolean;
  /** Show the discounted price as the current price. */
  readonly displayDiscountedPrice: boolean;
  /** Show a discount badge / percentage chip. */
  readonly showDiscountBadge: boolean;
  /** Show the "amount saved" line. */
  readonly showAmountSaved: boolean;
}

export const DISCOUNT_PRESENTATION: Readonly<Record<DiscountState, DiscountPresentation>> = Object.freeze({
  'no-discount': Object.freeze({
    strikeThroughOriginal: false,
    displayDiscountedPrice: true,
    showDiscountBadge: false,
    showAmountSaved: false,
  }),
  applied: Object.freeze({
    strikeThroughOriginal: true,
    displayDiscountedPrice: true,
    showDiscountBadge: true,
    showAmountSaved: true,
  }),
  ineligible: Object.freeze({
    strikeThroughOriginal: false,
    displayDiscountedPrice: false,
    showDiscountBadge: false,
    showAmountSaved: false,
  }),
  expired: Object.freeze({
    strikeThroughOriginal: false,
    displayDiscountedPrice: false,
    showDiscountBadge: false,
    showAmountSaved: false,
  }),
  'pending-quote': Object.freeze({
    strikeThroughOriginal: false,
    displayDiscountedPrice: false,
    showDiscountBadge: false,
    showAmountSaved: false,
  }),
  'quote-failed': Object.freeze({
    strikeThroughOriginal: false,
    displayDiscountedPrice: false,
    showDiscountBadge: false,
    showAmountSaved: false,
  }),
  // The re-quote result is rendered with the applied discount presentation
  // plus an explicit "Price updated" status copy (host UI owns the status).
  'updated-on-requote': Object.freeze({
    strikeThroughOriginal: true,
    displayDiscountedPrice: true,
    showDiscountBadge: true,
    showAmountSaved: true,
  }),
});

/** Unknown state names throw explicitly instead of silently defaulting. */
export function presentationForDiscountState(state: DiscountState): DiscountPresentation {
  const table = DISCOUNT_PRESENTATION as Readonly<Record<string, DiscountPresentation | undefined>>;
  const presentation = table[state];
  if (!presentation) {
    throw new TypeError(`Unknown kiosk discount state: ${String(state)}`);
  }
  return presentation;
}

/**
 * Which of the seven elements may be displayed for this field set: exactly the
 * fields present with a non-empty string value, in canonical key order.
 * Absent fields are omitted (never inferred); the returned keys are display
 * slots, not values — values stay wherever the host put them.
 */
export function presentDiscountFieldKeys(fields: DiscountDisplayFields): DiscountFieldKey[] {
  return DISCOUNT_FIELD_KEYS.filter((key) => {
    const value = fields[key];
    return typeof value === 'string' && value.trim().length > 0;
  });
}

/**
 * Convenience view for the host UI: present fields that the state allows to
 * render, plus whether the strike-through applies to the original price.
 * Pure selection — no arithmetic, no fabrication.
 */
export interface DisplayableDiscount {
  readonly state: DiscountState;
  readonly presentation: DiscountPresentation;
  readonly fields: DiscountDisplayFields;
  readonly displayedKeys: readonly DiscountFieldKey[];
}

export function selectDisplayableDiscount(fields: DiscountDisplayFields, state: DiscountState): DisplayableDiscount {
  const presentation = presentationForDiscountState(state);
  const present = presentDiscountFieldKeys(fields);
  const displayedKeys = present.filter((key) => {
    switch (key) {
      case 'discountedPrice':
        return presentation.displayDiscountedPrice;
      case 'amountSaved':
        return presentation.showAmountSaved;
      case 'percentOff':
        return presentation.showDiscountBadge;
      case 'originalPrice':
        // The struck-through original only reads as a discount cue when the
        // badge/“save” presentation is active; otherwise show it un-struck.
        return true;
      default:
        return true;
    }
  });
  return { state, presentation, fields, displayedKeys };
}
